import React, { useState, useEffect } from 'react';
import { Table, Button, Form, Modal, Row, Col, Badge, InputGroup, Spinner, Alert } from 'react-bootstrap';
import { FaEdit, FaTrash, FaSearch, FaPlus, FaBuilding, FaUsers, FaUserShield, FaBook, FaCheck, FaTimes, FaIdCard, FaShieldAlt } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { generateOrganizationToken } from '../../../utils/urlSecurity';
import api from '../../../api/apiClient';
import { toast } from 'react-toastify';
import { getAssetUrl } from '../../../utils/apiBase';

const getStaticFileUrl = (filePath) => {
  if (!filePath) return null;
  return getAssetUrl(filePath);
};

const ManageOrganization = () => {
  const navigate = useNavigate();
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [editingOrganization, setEditingOrganization] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [organizationToDelete, setOrganizationToDelete] = useState(null);
  const [show2faModal, setShow2faModal] = useState(false);
  const [organization2fa, setOrganization2fa] = useState(null);
  const [twofaLoading, setTwofaLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    email: '',
    phone: '',
    website: '',
    address_street: '',
    address_city: '',
    address_state: '',
    address_country: '',
    address_zip: '',
    primary_contact_name: '',
    primary_contact_email: '',
    primary_contact_phone: '',
    notes: '',
    status: 1,
    logo: null,
    initial_licenses: 10
  });
  const [imagePreview, setImagePreview] = useState(null);
  const [formErrors, setFormErrors] = useState({});

  const fetchOrganizations = async () => {
    try {
      setLoading(true);
      const response = await api.organizations.getAll();
      
      let organizationsData = [];
      if (response?.data?.data && Array.isArray(response.data.data)) {
        organizationsData = response.data.data;
      } else if (response?.data && Array.isArray(response.data)) {
        organizationsData = response.data;
      } else {
        organizationsData = [];
      }
      
      setOrganizations(organizationsData);
    } catch (error) {
      console.error('Error fetching organizations:', error);
      toast.error('Failed to load organizations: ' + (error.response?.data?.message || error.message));
      setOrganizations([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrganizations();
  }, []);

  const handleSearch = (e) => setSearchTerm(e.target.value);
  const handleFilterChange = (e) => setFilterStatus(e.target.value);

  const validateForm = () => {
    const errors = {};
    if (!formData.name.trim()) errors.name = 'Organization name is required';
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) errors.email = 'Invalid email';
    if (formData.primary_contact_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.primary_contact_email)) errors.primary_contact_email = 'Invalid contact email';
    if (formData.website && !formData.website.match(/^https?:\/\/.+/)) errors.website = 'Must be a valid URL starting with http/https';
    if (!editingOrganization) {
      const licenseCount = parseInt(formData.initial_licenses);
      if (isNaN(licenseCount) || licenseCount < 1) errors.initial_licenses = 'At least 1 license required';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleShowModal = async (organization = null) => {
    if (organization) {
      setEditingOrganization(organization);
      setFormData({
        name: organization.name || '',
        description: organization.description || '',
        email: organization.email || '',
        phone: organization.phone || '',
        website: organization.website || '',
        address_street: organization.address_street || '',
        address_city: organization.address_city || '',
        address_state: organization.address_state || '',
        address_country: organization.address_country || '',
        address_zip: organization.address_zip || '',
        primary_contact_name: organization.primary_contact_name || '',
        primary_contact_email: organization.primary_contact_email || '',
        primary_contact_phone: organization.primary_contact_phone || '',
        notes: organization.notes || '',
        status: organization.status,
        logo: null,
        initial_licenses: 10
      });
      setImagePreview(getStaticFileUrl(organization.logo_path));
    } else {
      setEditingOrganization(null);
      setFormData({
        name: '', description: '', email: '', phone: '', website: '',
        address_street: '', address_city: '', address_state: '', address_country: '', address_zip: '',
        primary_contact_name: '', primary_contact_email: '', primary_contact_phone: '',
        notes: '', status: 1, logo: null, initial_licenses: 10
      });
      setImagePreview(null);
    }
    setFormErrors({});
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingOrganization(null);
    setImagePreview(null);
    setFormErrors({});
  };

  const handleInputChange = (e) => {
    const { name, value, type, files } = e.target;
    if (type === 'file') {
      const file = files[0];
      if (file) {
        setFormData(prev => ({ ...prev, [name]: file }));
        const reader = new FileReader();
        reader.onload = (ev) => setImagePreview(ev.target.result);
        reader.readAsDataURL(file);
      }
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
    if (formErrors[name]) setFormErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setModalLoading(true);
    try {
      const submitData = new FormData();
      Object.keys(formData).forEach(key => {
        if (key === 'logo' && formData[key]) submitData.append('logo', formData[key]);
        else if (key !== 'logo' && formData[key] !== null && formData[key] !== '') submitData.append(key, formData[key]);
      });

      if (editingOrganization) {
        await api.organizations.update(editingOrganization.id, submitData);
        toast.success('Organization updated successfully!');
      } else {
        await api.organizations.create(submitData);
        const licenseCount = parseInt(formData.initial_licenses) || 0;
        toast.success(`Organization created! ${licenseCount > 0 ? `${licenseCount} licenses allocated.` : ''}`);
      }
      handleCloseModal();
      fetchOrganizations();
    } catch (error) {
      toast.error('Failed to save: ' + (error.response?.data?.message || error.message));
    } finally {
      setModalLoading(false);
    }
  };

  const handleDeleteClick = (org) => {
    setOrganizationToDelete(org);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!organizationToDelete) return;
    setDeleteLoading(organizationToDelete.id);
    try {
      await api.organizations.delete(organizationToDelete.id);
      toast.success('Organization deleted!');
      setShowDeleteModal(false);
      setOrganizationToDelete(null);
      fetchOrganizations();
    } catch (error) {
      toast.error('Failed to delete: ' + (error.response?.data?.message || error.message));
    } finally {
      setDeleteLoading(null);
    }
  };

  const handleRestoreClick = async (org) => {
    setDeleteLoading(org.id);
    try {
      await api.organizations.restore(org.id);
      toast.success('Organization restored!');
      fetchOrganizations();
    } catch (error) {
      toast.error('Failed to restore: ' + (error.response?.data?.message || error.message));
    } finally {
      setDeleteLoading(null);
    }
  };

  const handleShow2faModal = (org) => {
    setOrganization2fa(org);
    setShow2faModal(true);
  };

  const handleClose2faModal = () => {
    setShow2faModal(false);
    setOrganization2fa(null);
  };

  const handleOrganization2fa = async (enabled) => {
    if (!organization2fa) return;
    setTwofaLoading(true);
    try {
      await api.organizations.set2faEnabled(organization2fa.id, enabled);
      toast.success(enabled
        ? '2FA enabled for all organization users and admins (setup required on next login)'
        : '2FA disabled for all organization users and admins');
      handleClose2faModal();
      fetchOrganizations();
    } catch (error) {
      toast.error('Failed to update 2FA: ' + (error.response?.data?.message || error.message));
    } finally {
      setTwofaLoading(false);
    }
  };

  const handleNavigateToUsers = (id, name) => navigate(`/admin/organizations/${generateOrganizationToken(id)}/users`, { state: { organizationName: name } });
  const handleNavigateToAdmins = (id, name) => navigate(`/admin/organizations/${generateOrganizationToken(id)}/admins`, { state: { organizationName: name } });
  const handleNavigateToCourses = (id, name) => navigate(`/admin/organizations/${generateOrganizationToken(id)}/courses`, { state: { organizationName: name } });
  const handleNavigateToLicenses = (id, name) => navigate(`/admin/organizations/${generateOrganizationToken(id)}/licenses`, { state: { organizationName: name } });

  const filteredOrganizations = Array.isArray(organizations) ? organizations.filter(org => {
    if (!org) return false;
    const search = (searchTerm || '').toLowerCase();
    const matchesSearch = (org.name || '').toLowerCase().includes(search) || (org.email || '').toLowerCase().includes(search);
    const matchesStatus = filterStatus === 'all' ||
      (filterStatus === 'active' && org.status === 1 && !org.deleted) ||
      (filterStatus === 'inactive' && org.status === 0 && !org.deleted) ||
      (filterStatus === 'deleted' && org.deleted);
    return matchesSearch && matchesStatus;
  }) : [];

  return (
    <div className="p-3 sm:p-4 md:p-6 bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 min-h-screen">
      {/* Header */}
      <div className="mb-3">
        <h5 className="fw-bold mb-0 text-xl sm:text-2xl md:text-3xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600">
          Manage Organizations
        </h5>
      </div>

      {/* Search + Filter + Add */}
      <Row className="mb-3 g-3 align-items-center">
        <Col xs={12} md={6}>
          <InputGroup className="shadow-sm rounded-lg sm:rounded-xl overflow-hidden">
            <InputGroup.Text className="bg-white border-0 text-purple-600 px-3">
              <FaSearch className="w-5 h-5" />
            </InputGroup.Text>
            <Form.Control
              type="text"
              placeholder="Search by name or email..."
              value={searchTerm}
              onChange={handleSearch}
              className="border-0 focus:ring-2 focus:ring-purple-500 text-sm"
            />
          </InputGroup>
        </Col>
        <Col xs={12} sm={6} md={3}>
          <Form.Select 
            value={filterStatus} 
            onChange={handleFilterChange}
            className="rounded-lg sm:rounded-xl shadow-sm border-purple-200 focus:ring-2 focus:ring-purple-500 text-sm"
          >
            <option value="all">All Organizations</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="deleted">Deleted</option>
          </Form.Select>
        </Col>
        <Col xs={12} sm={6} md={3}>
          <Button
            bsPrefix="custom-hamburger"
            onClick={() => handleShowModal()}
            className="w-full border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 px-4 py-2"
          >
            <FaPlus className="w-5 h-5" />
            <span>Add Organization</span>
          </Button>
        </Col>
      </Row>

      {/* Table */}
      {loading ? (
        <div className="d-flex justify-content-center align-items-center py-16">
          <Spinner animation="border" className="w-12 h-12 text-purple-600" />
        </div>
      ) : filteredOrganizations.length === 0 ? (
        <Alert variant="info" className="text-center py-8 rounded-xl sm:rounded-2xl bg-white/80 backdrop-blur shadow-md border border-purple-100">
          <p className="text-sm sm:text-base font-medium text-purple-700">
            {searchTerm || filterStatus !== 'all' 
              ? 'No organizations found matching your criteria.' 
              : 'No organizations yet. Click "Add Organization" to create one.'
            }
          </p>
        </Alert>
      ) : (
        <div className="overflow-x-auto -mx-3 sm:mx-0 rounded-xl sm:rounded-2xl shadow-lg bg-white">
          <Table bordered hover className="m-0 min-w-max text-xs sm:text-sm">
            <thead className="bg-gradient-to-r from-purple-600 to-pink-600 text-white">
              <tr>
                <th className="py-3 px-3 text-left font-bold">Organization</th>
                <th className="py-3 px-3 text-left font-bold">Email</th>
                <th className="py-3 px-3 text-left font-bold">Phone</th>
                <th className="py-3 px-3 text-left font-bold">Status</th>
                <th className="py-3 px-3 text-left font-bold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredOrganizations.map((org) => (
                <tr key={org.id} className={`border-b border-purple-100 hover:bg-purple-50/50 transition-all ${org.deleted ? 'bg-red-50' : ''}`}>
                  <td className="py-3 px-3">
                    <div className="d-flex align-items-center gap-3">
                    
                      <div>
                        <div className="font-semibold text-gray-800">{org.name}</div>
                        {org.description && <div className="text-xs text-gray-600">{org.description.substring(0, 60)}...</div>}
                      </div>
                    </div>
                  </td>
                  <td className="py-3 px-3 text-gray-600">{org.email || '-'}</td>
                  <td className="py-3 px-3 text-gray-600">{org.phone || '-'}</td>
                  <td className="py-3 px-3 text-center">
                    <Badge bg={org.deleted ? 'danger' : (org.status === 1 ? 'success' : 'warning')} className="px-3 py-1 text-xs">
                      {org.deleted ? 'Deleted' : org.status === 1 ? 'Active' : 'Inactive'}
                    </Badge>
                  </td>
                  <td className="py-3 px-3">
                    <div className="d-flex gap-2 justify-content-left flex-wrap">
                      {!org.deleted ? (
                        <>
                          <Button bsPrefix="custom-hamburger" size="sm" onClick={() => handleShowModal(org)} title="Edit" className="border-purple-300 text-purple-600 hover:bg-purple-50 rounded p-1">
                            <FaEdit className="w-5 h-5" />
                          </Button>
                          <Button bsPrefix="custom-hamburger" size="sm" onClick={() => handleDeleteClick(org)} title="Delete" className="border-red-300 text-red-600 hover:bg-red-50 rounded p-1" disabled={deleteLoading === org.id}>
                            {deleteLoading === org.id ? <Spinner size="sm" /> : <FaTrash className="w-5 h-5" />}
                          </Button>
                          <Button bsPrefix="custom-hamburger" size="sm" onClick={() => handleNavigateToUsers(org.id, org.name)} title="Org Learners" className="border-blue-300 text-blue-600 hover:bg-blue-50 rounded p-1">
                            <FaUsers className="w-5 h-5" />
                          </Button>
                          <Button bsPrefix="custom-hamburger" size="sm" onClick={() => handleNavigateToAdmins(org.id, org.name)} title="Org Admin/Instructor user" className="border-orange-300 text-orange-600 hover:bg-orange-50 rounded p-1">
                            <FaUserShield className="w-5 h-5" />
                          </Button>
                          <Button bsPrefix="custom-hamburger" size="sm" onClick={() => handleNavigateToCourses(org.id, org.name)} title="Courses" className="border-green-300 text-green-600 hover:bg-green-50 rounded p-1">
                            <FaBook className="w-5 h-5" />
                          </Button>
                          <Button bsPrefix="custom-hamburger" size="sm" onClick={() => handleNavigateToLicenses(org.id, org.name)} title="Licenses" className="border-cyan-300 text-cyan-600 hover:bg-cyan-50 rounded p-1">
                            <FaIdCard className="w-5 h-5" />
                          </Button>
                          <Button bsPrefix="custom-hamburger" size="sm" onClick={() => handleShow2faModal(org)} title="Manage 2FA" className="border-indigo-300 text-indigo-600 hover:bg-indigo-50 rounded p-1">
                            <FaShieldAlt className="w-5 h-5" />
                          </Button>
                        </>
                      ) : (
                        <Button bsPrefix="custom-hamburger" size="sm" onClick={() => handleRestoreClick(org)} title="Restore" className="border-green-300 text-green-600 hover:bg-green-50 rounded p-1" disabled={deleteLoading === org.id}>
                          {deleteLoading === org.id ? <Spinner size="sm" /> : <FaCheck className="w-5 h-5" />}
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}

      {/* Add/Edit Modal */}
      <Modal show={showModal} onHide={handleCloseModal} size="lg" centered>
        <Modal.Header closeButton closeVariant="white" className="bg-gradient-to-r from-purple-600 to-pink-600 text-white border-0 py-3 px-4 sm:px-4 sm:py-4 modal-header">
          <Modal.Title className="font-bold text-xl text-white">
            {editingOrganization ? 'Edit Organization' : 'Add New Organization'}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body className="bg-gray-50 p-3 sm:p-5">
            {/* Basic Information */}
            <Row className="g-4 mb-4">
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="font-semibold text-gray-700">Name <span className="text-danger">*</span></Form.Label>
                  <Form.Control type="text" name="name" value={formData.name} onChange={handleInputChange} isInvalid={!!formErrors.name} placeholder="Acme Corp" className="rounded-lg border-purple-200 focus:ring-purple-500" />
                  <Form.Control.Feedback type="invalid">{formErrors.name}</Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="font-semibold text-gray-700">Status</Form.Label>
                  <Form.Select name="status" value={formData.status} onChange={handleInputChange} className="rounded-lg border-purple-200 focus:ring-purple-500">
                    <option value={1}>Active</option>
                    <option value={0}>Inactive</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            {!editingOrganization && (
              <Row className="mb-4">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="font-semibold text-gray-700">Initial User Licenses <span className="text-danger">*</span></Form.Label>
                    <Form.Control type="number" name="initial_licenses" value={formData.initial_licenses} onChange={handleInputChange} isInvalid={!!formErrors.initial_licenses} min="1" className="rounded-lg border-purple-200 focus:ring-purple-500" />
                    <Form.Control.Feedback type="invalid">{formErrors.initial_licenses}</Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>
            )}

            <Form.Group className="mb-4">
              <Form.Label className="font-semibold text-gray-700">Description</Form.Label>
              <Form.Control as="textarea" rows={3} name="description" value={formData.description} onChange={handleInputChange} placeholder="About the organization..." className="rounded-lg border-purple-200 focus:ring-purple-500" />
            </Form.Group>

            {/* Contact Information */}
            <h6 className="font-semibold text-gray-800 mb-3 mt-4">Contact Information</h6>
            <Row className="g-4 mb-4">
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="font-semibold text-gray-700">Email</Form.Label>
                  <Form.Control type="email" name="email" value={formData.email} onChange={handleInputChange} isInvalid={!!formErrors.email} placeholder="contact@organization.com" className="rounded-lg border-purple-200 focus:ring-purple-500" />
                  <Form.Control.Feedback type="invalid">{formErrors.email}</Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="font-semibold text-gray-700">Phone</Form.Label>
                  <Form.Control type="text" name="phone" value={formData.phone} onChange={handleInputChange} placeholder="+1 (555) 123-4567" className="rounded-lg border-purple-200 focus:ring-purple-500" />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-4">
              <Form.Label className="font-semibold text-gray-700">Website</Form.Label>
              <Form.Control type="url" name="website" value={formData.website} onChange={handleInputChange} isInvalid={!!formErrors.website} placeholder="https://www.organization.com" className="rounded-lg border-purple-200 focus:ring-purple-500" />
              <Form.Control.Feedback type="invalid">{formErrors.website}</Form.Control.Feedback>
            </Form.Group>

            {/* Address Information */}
            <h6 className="font-semibold text-gray-800 mb-3 mt-4">Address Information</h6>
            <Form.Group className="mb-4">
              <Form.Label className="font-semibold text-gray-700">Street Address</Form.Label>
              <Form.Control type="text" name="address_street" value={formData.address_street} onChange={handleInputChange} placeholder="123 Main Street" className="rounded-lg border-purple-200 focus:ring-purple-500" />
            </Form.Group>

            <Row className="g-4 mb-4">
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="font-semibold text-gray-700">City</Form.Label>
                  <Form.Control type="text" name="address_city" value={formData.address_city} onChange={handleInputChange} placeholder="New York" className="rounded-lg border-purple-200 focus:ring-purple-500" />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="font-semibold text-gray-700">State/Province</Form.Label>
                  <Form.Control type="text" name="address_state" value={formData.address_state} onChange={handleInputChange} placeholder="NY" className="rounded-lg border-purple-200 focus:ring-purple-500" />
                </Form.Group>
              </Col>
            </Row>

            <Row className="g-4 mb-4">
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="font-semibold text-gray-700">Country</Form.Label>
                  <Form.Control type="text" name="address_country" value={formData.address_country} onChange={handleInputChange} placeholder="United States" className="rounded-lg border-purple-200 focus:ring-purple-500" />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="font-semibold text-gray-700">ZIP/Postal Code</Form.Label>
                  <Form.Control type="text" name="address_zip" value={formData.address_zip} onChange={handleInputChange} placeholder="10001" className="rounded-lg border-purple-200 focus:ring-purple-500" />
                </Form.Group>
              </Col>
            </Row>

            {/* Primary Contact */}
            <h6 className="font-semibold text-gray-800 mb-3 mt-4">Primary Contact</h6>
            <Row className="g-4 mb-4">
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="font-semibold text-gray-700">Contact Name</Form.Label>
                  <Form.Control type="text" name="primary_contact_name" value={formData.primary_contact_name} onChange={handleInputChange} placeholder="John Doe" className="rounded-lg border-purple-200 focus:ring-purple-500" />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="font-semibold text-gray-700">Contact Email</Form.Label>
                  <Form.Control type="email" name="primary_contact_email" value={formData.primary_contact_email} onChange={handleInputChange} isInvalid={!!formErrors.primary_contact_email} placeholder="john.doe@organization.com" className="rounded-lg border-purple-200 focus:ring-purple-500" />
                  <Form.Control.Feedback type="invalid">{formErrors.primary_contact_email}</Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-4">
              <Form.Label className="font-semibold text-gray-700">Contact Phone</Form.Label>
              <Form.Control type="text" name="primary_contact_phone" value={formData.primary_contact_phone} onChange={handleInputChange} placeholder="+1 (555) 123-4567" className="rounded-lg border-purple-200 focus:ring-purple-500" />
            </Form.Group>

            {/* Additional Information */}
            <Form.Group className="mb-4">
              <Form.Label className="font-semibold text-gray-700">Notes</Form.Label>
              <Form.Control as="textarea" rows={3} name="notes" value={formData.notes} onChange={handleInputChange} placeholder="Additional notes about the organization..." className="rounded-lg border-purple-200 focus:ring-purple-500" />
            </Form.Group>

            {/* Logo Upload */}
            <Form.Group className="mb-4">
              <Form.Label className="font-semibold text-gray-700">Logo</Form.Label>
              <Form.Control type="file" name="logo" onChange={handleInputChange} accept="image/*" className="rounded-lg" />
              {imagePreview && (
                <div className="mt-3">
                  <img src={imagePreview} alt="Preview" className="max-w-xs max-h-32 rounded-lg shadow-md" />
                </div>
              )}
            </Form.Group>
          </Modal.Body>
          <Modal.Footer className="bg-gray-100 border-0 rounded-b-2xl p-4">
            <Row className="w-100 g-3">
              <Col md={6}>
                <Button bsPrefix="custom-hamburger" onClick={handleCloseModal} disabled={modalLoading} className="w-full border-1 bg-gray-100 hover:bg-gray-200 text-gray-700 border-2 border-gray-300 hover:border-gray-500 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2">
                  Cancel
                </Button>
              </Col>
              <Col md={6}>
                <Button bsPrefix="custom-hamburger" type="submit" disabled={modalLoading} className="w-full border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2">
                  {modalLoading ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      {editingOrganization ? 'Updating...' : 'Creating...'}
                    </>
                  ) : (
                    editingOrganization ? 'Update Organization' : 'Create Organization'
                  )}
                </Button>
              </Col>
            </Row>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Delete Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <Modal.Header closeButton closeVariant="white" className="bg-gradient-to-r from-purple-600 to-pink-600 text-white border-0 py-3 px-4 sm:px-4 sm:py-4 modal-header">
          <Modal.Title className="font-bold text-xl text-white">
            Confirm Delete
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center py-10 bg-gray-50">
          <div className="mb-4">
            <FaTrash size={60} className="text-red-500 mx-auto" />
          </div>
          <h5 className="font-bold text-gray-800 mb-3">Delete Organization</h5>
          <p className="text-gray-600">
            Are you sure you want to delete{' '}
            <strong>{organizationToDelete?.name}</strong>?
          </p>
          <p className="text-sm text-gray-500">This is a soft delete. The organization can be restored later.</p>
        </Modal.Body>
        <Modal.Footer className="bg-gray-100 border-0 rounded-b-2xl p-4">
          <Row className="w-100 g-3">
            <Col md={6}>
              <Button bsPrefix="custom-hamburger" onClick={() => setShowDeleteModal(false)} className="w-full border-1 bg-gray-100 hover:bg-gray-200 text-gray-700 border-2 border-gray-300 hover:border-gray-500 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2">
                Cancel
              </Button>
            </Col>
            <Col md={6}>
              <Button bsPrefix="custom-hamburger" onClick={handleDeleteConfirm} disabled={deleteLoading} className="w-full border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2">
                {deleteLoading ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Deleting...
                  </>
                ) : (
                  'Delete Organization'
                )}
              </Button>
            </Col>
          </Row>
        </Modal.Footer>
      </Modal>

      {/* Manage 2FA Modal */}
      <Modal show={show2faModal} onHide={handleClose2faModal} centered>
        <Modal.Header closeButton closeVariant="white" className="bg-gradient-to-r from-purple-600 to-pink-600 text-white border-0 py-3 px-4 sm:px-4 sm:py-4 modal-header">
          <Modal.Title className="font-bold text-xl text-white">
            Manage 2FA
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center py-10 bg-gray-50">
          <div className="mb-4">
            <FaShieldAlt size={60} className="text-indigo-500 mx-auto" />
          </div>
          <h5 className="font-bold text-gray-800 mb-3">Organization-wide 2FA</h5>
          <p className="text-gray-600">
            Enable or disable two-factor authentication for all users and admins of{' '}
            <strong>{organization2fa?.name}</strong>.
          </p>
          <p className="text-sm text-gray-500">Enabling forces every member to set up an authenticator on next login. Disabling removes their existing 2FA configuration.</p>
        </Modal.Body>
        <Modal.Footer className="bg-gray-100 border-0 rounded-b-2xl p-4">
          <Row className="w-100 g-3">
            <Col md={6}>
              <Button bsPrefix="custom-hamburger" onClick={() => handleOrganization2fa(false)} disabled={twofaLoading} className="w-full border-1 bg-gray-100 hover:bg-gray-200 text-gray-700 border-2 border-gray-300 hover:border-gray-500 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2">
                {twofaLoading ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Updating...
                  </>
                ) : (
                  'Disable for All'
                )}
              </Button>
            </Col>
            <Col md={6}>
              <Button bsPrefix="custom-hamburger" onClick={() => handleOrganization2fa(true)} disabled={twofaLoading} className="w-full border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2">
                {twofaLoading ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Updating...
                  </>
                ) : (
                  'Enable for All'
                )}
              </Button>
            </Col>
          </Row>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ManageOrganization;